import React from 'react';
import { formatAgentValue } from '../lib/agentUnits';
import type { SeriesUnit, TableSpec } from '../types';
import { Icon } from './shared/Icon';

// Rows rendered inline in the chat; the full data set is always available via
// the CSV download, so we keep the DOM light for large exports.
const PREVIEW_ROWS = 100;

function formatCell(value: string | number | undefined, unit?: SeriesUnit): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  return formatAgentValue(value, unit, 2);
}

function csvCell(value: string | number | undefined): string {
  if (value === undefined || value === null) return '';
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function slugify(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'export'
  );
}

export function downloadCsv(spec: TableSpec): void {
  const rows = spec.data ?? [];
  const header = spec.columns.map(c => csvCell(c.label)).join(',');
  const body = rows
    .map(row => spec.columns.map(c => csvCell(row[c.key])).join(','))
    .join('\n');
  // Prepend a UTF-8 BOM so Excel opens accented labels correctly.
  const csv = `\ufeff${header}\n${body}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slugify(spec.title)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Renders a server-hydrated TableSpec: a preview of the rows plus a CSV download
 * of the full data set. Values come from `spec.data` (hydrated from a real tool
 * result on the backend); this component only formats and exports them.
 */
export function TableRenderer({ spec }: { spec: TableSpec }) {
  const data = spec.data ?? [];
  if (spec.columns.length === 0) {
    return null;
  }
  if (data.length === 0 && spec.rowCount !== 0) {
    return null;
  }

  const total = spec.rowCount ?? data.length;
  const preview = data.slice(0, PREVIEW_ROWS);
  const hiddenInline = data.length - preview.length;

  return (
    <figure className="agent-table">
      <figcaption className="agent-table-head">
        <span className="agent-table-title">
          <Icon name="table" size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {spec.title}
        </span>
        <button
          type="button"
          className="agent-table-dl"
          onClick={() => downloadCsv(spec)}
          title="Download all rows as CSV"
        >
          <Icon name="arrowDown" size={13} />
          CSV
        </button>
      </figcaption>

      <div className="agent-table-scroll">
        <table className="agent-table-grid">
          <thead>
            <tr>
              {spec.columns.map(c => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((row, i) => (
              <tr key={i}>
                {spec.columns.map(c => (
                  <td key={c.key}>{formatCell(row[c.key], c.unit)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="agent-table-foot">
        {hiddenInline > 0
          ? `Showing first ${preview.length} of ${total.toLocaleString('de-CH')} rows - download the CSV for all.`
          : `${total.toLocaleString('de-CH')} row${total === 1 ? '' : 's'}.`}
        {spec.truncated ? ' (Result capped.)' : ''}
      </div>
    </figure>
  );
}
